import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateInterventionList,
  updateIndexOnList,
  resetError,
  updateIsShowBioSimilarToggle,
} from './SwitchList.actions';
import ResetListState from 'components/ResetListState';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { DEFAULT_SWIM_SORT, SWIM_FILTER } from 'lookup/SwimConstants';
import SwitchListCommon from './SwitchListCommon';
import { COLUMN } from './CreateColumnListForSwitchList';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Form } from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import { isLoggedInUserBioSimilarUser, isLoggedInUserAWAdmin } from 'services/AWUtilityService';

const DEFAULT_PAGE_SIZE = 15;

const COLUMN_LIST = [
  COLUMN.customerNameAbbreviation,
  COLUMN.clientNameAbbreviation,
  COLUMN.switchNumber,
  COLUMN.state,
  COLUMN.status,
  COLUMN.prescriberCallCounter,
  COLUMN.daysAfterLastModifiedDate,
  COLUMN.lastModifiedDate,
  COLUMN.pharmacyName,
  COLUMN.pharmacyCallCounter,
  COLUMN.isPickupConfirmed,
  COLUMN.memberNumberWithoutCustId,
  COLUMN.memberNumber,
  COLUMN.memberCallCounter,
  COLUMN.effectiveDate,
  COLUMN.projectedSavings,
  COLUMN.prescriberName,
  COLUMN.prescriberPhone,
  COLUMN.prescriberFax,
  COLUMN.pharmacyPhone,
  COLUMN.prescriberNPI,
  COLUMN.pharmacyFax,
  COLUMN.pharmacyNPI,
];

const SwitchList = () => {
  const dispatch = useDispatch();

  const { data, pages, totalElements, loading, listState, isError, isShowBioSimilarToggle } = useSelector((state) => {
    let listState = state.switchList.listState;
    if (!listState) {
      listState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: DEFAULT_SWIM_SORT,
        filtered:
          isLoggedInUserBioSimilarUser() && !isLoggedInUserAWAdmin()
            ? [...SWIM_FILTER.defualt, { id: 'priorityAgent', value: true }]
            : [...SWIM_FILTER.defualt, { id: 'priorityAgent', value: false }],
        resized: [],
        expanded: {},
      };
    }
    return {
      data: state.switchList.data !== undefined ? state.switchList.data : [],
      pages: state.switchList.pages ? state.switchList.pages : 0,
      totalElements:
        state.switchList.totalElements !== undefined ? state.switchList.totalElements : 'Loading ...',
      loading: state.switchList.loading,
      listState: listState,
      isError: state.switchList.isError,
      isShowBioSimilarToggle: state.switchList.isShowBioSimilar ?? undefined,
    };
  });

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const resetList = useCallback(() => {
    const { pageSize, resized, expanded } = listState;
    let bioSimilarToggle = isShowBioSimilarToggle;
    if (isLoggedInUserBioSimilarUser() && !isLoggedInUserAWAdmin()) {
      bioSimilarToggle = true;
      dispatch(
        updateInterventionList(
          pageSize,
          0,
          DEFAULT_SWIM_SORT,
          [...SWIM_FILTER.defualt, { id: 'priorityAgent', value: true }, { id: 'customerNameAbbreviation', value: 'WMT' }, { id: 'clientNameAbbreviation', value: 'WMT' }],
          resized,
          expanded
        )
      );
    } else {
      bioSimilarToggle = false;
      dispatch(
        updateInterventionList(
          pageSize,
          0,
          DEFAULT_SWIM_SORT,
          [...SWIM_FILTER.defualt, { id: 'priorityAgent', value: false }],
          resized,
          expanded
        )
      );
    }
    dispatch(updateIsShowBioSimilarToggle(bioSimilarToggle));
  }, [listState, isShowBioSimilarToggle, dispatch]);

  useEffect(() => {
    if (isShowBioSimilarToggle === undefined) {
      const isBioSimilarUser = isLoggedInUserBioSimilarUser();
      const isAdmin = isLoggedInUserAWAdmin();
      const bioSimilarToggle = isBioSimilarUser && !isAdmin;
      dispatch(updateIsShowBioSimilarToggle(bioSimilarToggle));
    }
    setIsInitialLoadComplete(true);
  }, [isShowBioSimilarToggle, dispatch]);

  const handleBioSimilarToggle = useCallback(
    (e) => {
      const { filtered } = listState;
      let newFiltered = [...SWIM_FILTER.defualt];

      if (e.target.checked) {
        newFiltered = newFiltered.filter((element) => !['priorityAgent', 'customerNameAbbreviation', 'clientNameAbbreviation'].includes(element.id));
        newFiltered.push(
          { id: 'priorityAgent', value: true },
          { id: 'customerNameAbbreviation', value: 'WMT' },
          { id: 'clientNameAbbreviation', value: 'WMT' }
        );
        if (JSON.stringify(newFiltered) !== JSON.stringify(filtered)) {
          dispatch(
            updateInterventionList(listState.pageSize, listState.page, listState.sorted, newFiltered, listState.resized, listState.expanded)
          );
        }
      } else {
        newFiltered = newFiltered.filter((element) => !['priorityAgent', 'customerNameAbbreviation', 'clientNameAbbreviation'].includes(element.id));
        newFiltered.push({ id: 'priorityAgent', value: false });
        if (JSON.stringify(newFiltered) !== JSON.stringify(filtered)) {
          dispatch(
            updateInterventionList(listState.pageSize, listState.page, listState.sorted, newFiltered, listState.resized, listState.expanded)
          );
        }
      }
      dispatch(updateIsShowBioSimilarToggle(e.target.checked));
    },
    [listState, dispatch]
  );

  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div
            className="codex-version-display-bar"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ float: 'left' }}>
              Total Switches: <strong>{totalElements}</strong>
            </span>
            <strong style={{ textAlign: 'center' }}>Switch List</strong>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                float: 'right',
              }}
            >
              {isLoggedInUserBioSimilarUser() && (
                <span className="tiny-bottom-spacer" style={{ marginRight: '15px' }}>
                  <OverlayTrigger
                    overlay={<Tooltip>Toggling this ON will show Bio Similar Switches</Tooltip>}
                  >
                    <Form.Check
                      type="switch"
                      className="aw-bio-similar-callgroups-toggle"
                      id="toggleBioSimilarRef"
                      label={`Bio Similar Switches`}
                      onChange={handleBioSimilarToggle}
                      checked={isShowBioSimilarToggle}
                    />
                  </OverlayTrigger>
                </span>
              )}
              <ResetListState onReset={resetList} />
            </div>
          </div>
        </Col>
      </Row>
      <SwitchListCommon
        updateInterventionList={(...args) => dispatch(updateInterventionList(...args))}
        updateIndexOnList={(...args) => dispatch(updateIndexOnList(...args))}
        resetError={() => dispatch(resetError())}
        data={data}
        pages={pages}
        totalElements={totalElements}
        loading={loading}
        listState={listState}
        isError={isError}
        columnList={COLUMN_LIST}
        isShowBioSimilarToggle={isShowBioSimilarToggle}
        isSortFilterEnabledForCust={true}
        isInitialLoadComplete={isInitialLoadComplete}
      />
    </Container>
  );
};

export default SwitchList;